import { FatalDecoratorLimitDescriptorError } from '@rehaje/reactive'
import {
  createMemberDecorator,
  ensureProtoProps,
  isInheritProto
} from '@rehaje/shared'

import { ViewModel } from '../models/ViewModel'

import type { MixtureMemberDecorator } from '@rehaje/shared'

/**
 * 从 TypedNode 中同步属性的 TS 装饰器，只能用于装饰 ViewModel 子类属性
 * @example
 * // jsdoc 限制，实际使用请将 * 替换为 @
 * class MyModel extends ViewModel {
 *   *fromNode()
 *   *reactive()
 *   test = 123
 * }
 * @param from 重定向属性名，默认与当前装饰属性一致
 */
export const fromNode: () => MixtureMemberDecorator = createMemberDecorator({
  tsDecorator(proto: ViewModel, key, descriptor) {
    if (!isInheritProto(proto, ViewModel.prototype)) {
      throw new FatalDecoratorLimitDescriptorError('fromNode', key, 'ViewModel')
    }

    const fromKeys = ensureProtoProps(proto, '__r_from_keys', [])
    if (!fromKeys.includes(key)) fromKeys.push(key)
    const fromMaps = ensureProtoProps(proto, '__r_from_maps', {})
    fromMaps[key] = true

    const fromNodeKey = `__r_from_node_${key}`
    const assignedKey = `__r_assigned_${key}`

    if (typeof descriptor?.value === 'function') {
      return descriptor
    }

    const get =
      descriptor?.get ??
      new Function(
        `
        return this.${fromNodeKey}
        `
      )
    const set =
      descriptor?.set ??
      new Function(
        'value',
        `
        this.${fromNodeKey} = value
        `
      )

    return {
      ...descriptor,
      get,
      set: new Function(
        'set',
        `
        return function(value){
          if (!this.__r_initialized) {
            if (this.${assignedKey} && this.__r_assigned) return
            this.${assignedKey} = true
          }
          set.call(this, value)
        }
        `
      )(set)
    } as PropertyDescriptor
  }
})
